import { IssuerData, CurrencyData, Product, ExpiryData, EventsDashboard, EventDetail, CalendarDayEvents, InAppNotification, NotificationPreferences, NotificationEmailPreview } from '../types';
import {
  MonthlyOverviewAnalytics,
  UserMonthlyAnalyticsDetail,
  UserMonthlyAnalyticsRow,
} from '../types/userAnalytics';
import {
  AddFavouriteRequest,
  FavouriteCreatedResponse,
  FavouriteItem,
  OwnershipScope,
  WatchlistItem,
} from '../types/favourites';
import { ProductShareLinkCreated } from '../types/productShare';
import { DashboardLayout, DashboardLayoutResponse } from '../config/dashboardWidgets/types';
import { Platform } from 'react-native';
import authService from './authService';
import { API_CONFIG } from '../config/api';
import { withAuthErrorHandling } from '../utils/authInterceptor';

export interface CustomReportColleague {
  id: string;
  email: string;
  name: string;
}

export interface CustomReportListItem {
  id: string;
  name: string;
  ownerUserId: string;
  isOwner: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface CustomReportProduct {
  PROId: number;
  name: string;
  isin: string;
  valor?: string;
  bid: number;
  ask: number;
  ccy: string;
  issuer?: string;
  productType?: number;
  productCode?: string;
  productTypeDisplay?: string;
  initialFixing?: string;
  finalFixing?: string;
  redemptionDate?: string;
  tradedNotional?: number | null;
  currentNotional?: number | null;
  allocatedNotional: number;
  maxNotional: number;
}

export interface CustomReportDetail extends CustomReportListItem {
  members: CustomReportColleague[];
  products: CustomReportProduct[];
}

class ApiService {
  private readonly baseUrl: string;

  constructor(baseUrl?: string) {
    const config = API_CONFIG;
    this.baseUrl = baseUrl || config.BASE_URL;
  }

  private async makeRequest<T>(endpoint: string): Promise<T> {
    try {
      const response = await authService.makeAuthenticatedRequest<{ success: boolean; data: T; error?: string }>(
        `${this.baseUrl}${endpoint}`
      );
      
      if (!response.success) {
        throw new Error(response.error || 'Server response error');
      }
      
      return response.data;
    } catch (error) {
      console.error('API request error:', error);
      throw error;
    }
  }

  async getStatisticsByIssuer(
    includeWarrants: boolean = true,
    ownershipScope: 'group' | 'self' = 'group'
  ): Promise<IssuerData[]> {
    return withAuthErrorHandling(async () => {
      const params = new URLSearchParams();
      params.append('includeWarrants', includeWarrants.toString());
      params.append('ownershipScope', ownershipScope);
      const raw = await this.makeRequest<Array<{ id?: number; name: string; quantity: number; percentage: number }>>(
        `${API_CONFIG.ENDPOINTS.STATISTICS.ISSUER_RANKING}?${params.toString()}`
      );
      return raw.map(item => ({
        issuerName: item.name,
        numberOfProducts: item.quantity,
        allocation: item.percentage,
      }));
    });
  }

  async getStatisticsByIssuerNotional(
    includeWarrants: boolean = true,
    ownershipScope: 'group' | 'self' = 'group',
    displayCurrency?: string
  ): Promise<IssuerData[]> {
    return withAuthErrorHandling(async () => {
      const params = new URLSearchParams();
      params.append('includeWarrants', includeWarrants.toString());
      params.append('ownershipScope', ownershipScope);
      if (displayCurrency) {
        params.append('displayCurrency', displayCurrency);
      }
      const raw = await this.makeRequest<
        Array<{
          id?: number;
          name: string;
          quantity: number;
          percentage: number;
          displayCurrency?: string;
        }>
      >(
        `${API_CONFIG.ENDPOINTS.STATISTICS.ISSUER_RANKING_NOTIONAL}?${params.toString()}`
      );
      return raw.map(item => ({
        issuerName: item.name,
        numberOfProducts: item.quantity, // In this case it's the total notional value
        allocation: item.percentage,
        displayCurrency: item.displayCurrency,
      }));
    });
  }

  async getNotionalByCurrency(
    includeWarrants: boolean = true,
    ownershipScope: 'group' | 'self' = 'group'
  ): Promise<CurrencyData[]> {
    return withAuthErrorHandling(async () => {
      const params = new URLSearchParams();
      params.append('includeWarrants', includeWarrants.toString());
      params.append('ownershipScope', ownershipScope);
      const raw = await this.makeRequest<
        Array<{
          id?: number;
          code: string;
          name: string;
          quantity: number; // CHF
          originalQuantity?: number; 
          percentage: number;
        }>
      >(
        `${API_CONFIG.ENDPOINTS.STATISTICS.NOTIONAL_BY_CURRENCY}?${params.toString()}`
      );
      return raw.map(item => ({
        currencyCode: item.code, // Short code (USD, EUR, CHF)
        currencyName: item.name, // Full name (United States Dollar)
        numberOfProducts: item.quantity, // Total notional value in this currency
        allocation: item.percentage,
        notionalOriginal: item.originalQuantity,
      }));
    });
  }

  async getProductsNearExpiry(
    includeWarrants: boolean = true,
    ownershipScope: 'group' | 'self' = 'group'
  ): Promise<ExpiryData[]> {
    return withAuthErrorHandling(async () => {
      const params = new URLSearchParams();
      params.append('includeWarrants', includeWarrants.toString());
      params.append('ownershipScope', ownershipScope);
      const raw = await this.makeRequest<Array<{ id: number; period: string; count: number; sortOrder: number }>>(
        `${API_CONFIG.ENDPOINTS.STATISTICS.PRODUCTS_NEAR_EXPIRY}?${params.toString()}`
      );
      return raw.map(item => ({
        id: item.id,
        period: item.period,
        count: item.count,
        sortOrder: item.sortOrder,
      }));
    });
  }

  /**
   * weekStart: Monday date in YYYY-MM-DD format (any week of the year).
   * Legacy: pass 'current' or 'next' and it will be converted to the corresponding Monday.
   */
  async getEventsDashboard(
    weekStart: string,
    includeWarrants: boolean = true,
    ownershipScope: 'group' | 'self' = 'group'
  ): Promise<EventsDashboard> {
    return withAuthErrorHandling(async () => {
      const resolved = this.resolveWeekStart(weekStart);
      const params = new URLSearchParams();
      params.append('weekStart', resolved);
      params.append('includeWarrants', includeWarrants.toString());
      params.append('ownershipScope', ownershipScope);
      const dashboardEndpoint = API_CONFIG.ENDPOINTS.EVENTS?.DASHBOARD || '/api/events/dashboard';
      return this.makeRequest<EventsDashboard>(
        `${dashboardEndpoint}?${params.toString()}`
      );
    });
  }

  async getEventsList(
    weekStart: string,
    includeWarrants: boolean = true,
    ownershipScope: 'group' | 'self' = 'group'
  ): Promise<EventDetail[]> {
    return withAuthErrorHandling(async () => {
      const resolved = this.resolveWeekStart(weekStart);
      const params = new URLSearchParams();
      params.append('weekStart', resolved);
      params.append('includeWarrants', includeWarrants.toString());
      params.append('ownershipScope', ownershipScope);
      const listEndpoint = API_CONFIG.ENDPOINTS.EVENTS?.LIST || '/api/events/list';
      return this.makeRequest<EventDetail[]>(
        `${listEndpoint}?${params.toString()}`
      );
    });
  }

  async getEventsListRange(
    fromDate: string,
    toDate: string,
    includeWarrants: boolean = false,
    ownershipScope: 'group' | 'self' = 'group'
  ): Promise<EventDetail[]> {
    return withAuthErrorHandling(async () => {
      const params = new URLSearchParams();
      params.append('from', fromDate);
      params.append('to', toDate);
      params.append('includeWarrants', includeWarrants.toString());
      params.append('ownershipScope', ownershipScope);
      const listEndpoint = API_CONFIG.ENDPOINTS.EVENTS?.LIST || '/api/events/list';
      return this.makeRequest<EventDetail[]>(
        `${listEndpoint}?${params.toString()}`
      );
    });
  }

  async getEventsCalendar(
    fromDate: string,
    toDate: string,
    includeWarrants: boolean = true,
    ownershipScope: 'group' | 'self' = 'group'
  ): Promise<CalendarDayEvents[]> {
    return withAuthErrorHandling(async () => {
      const params = new URLSearchParams();
      params.append('from', fromDate);
      params.append('to', toDate);
      params.append('includeWarrants', includeWarrants.toString());
      params.append('ownershipScope', ownershipScope);
      const calendarEndpoint = API_CONFIG.ENDPOINTS.EVENTS?.CALENDAR || '/api/events/calendar';
      return this.makeRequest<CalendarDayEvents[]>(
        `${calendarEndpoint}?${params.toString()}`
      );
    });
  }

  /** Convert 'current'|'next' or YYYY-MM-DD to Monday YYYY-MM-DD. */
  private resolveWeekStart(weekStart: string): string {
    if (weekStart === 'current' || weekStart === 'next') {
      const now = new Date();
      const day = now.getDay();
      const mondayOffset = day === 0 ? -6 : 1 - day;
      const thisMonday = new Date(now);
      thisMonday.setDate(now.getDate() + mondayOffset);
      if (weekStart === 'next') {
        thisMonday.setDate(thisMonday.getDate() + 7);
      }
      return thisMonday.toISOString().slice(0, 10);
    }
    const d = new Date(weekStart + 'T12:00:00');
    const dow = d.getDay();
    const diff = d.getDate() - (dow === 0 ? 6 : dow - 1);
    d.setDate(diff);
    return d.toISOString().slice(0, 10);
  }

  async getProducts(): Promise<Product[]> {
    return withAuthErrorHandling(async () => {
      return this.makeRequest<Product[]>(API_CONFIG.ENDPOINTS.STATISTICS.PRODUCTS);
    });
  }

  async getProductsByIssuer(issuerId: number): Promise<Product[]> {
    return withAuthErrorHandling(async () => {
      return this.makeRequest<Product[]>(`${API_CONFIG.ENDPOINTS.STATISTICS.ISSUER_RANKING.replace('/issuer-ranking', '')}/issuers/${issuerId}/products`);
    });
  }

  async getFilterOptions(): Promise<{ productTypes: number[]; issuers: string[] }> {
    return withAuthErrorHandling(async () => {
      const response = await this.makeRequest<{ productTypes: number[]; issuers: string[] }>(
        API_CONFIG.ENDPOINTS.PRODUCTS.FILTER_OPTIONS
      );
      // El endpoint devuelve { success: true, data: {...} }, pero makeRequest ya extrae el data
      return response;
    });
  }

  async getCustomReports(): Promise<CustomReportListItem[]> {
    return withAuthErrorHandling(async () => {
      return this.makeRequest<CustomReportListItem[]>(API_CONFIG.ENDPOINTS.CUSTOM_REPORTS.LIST);
    });
  }

  async getCustomReportColleagues(): Promise<CustomReportColleague[]> {
    return withAuthErrorHandling(async () => {
      return this.makeRequest<CustomReportColleague[]>(
        API_CONFIG.ENDPOINTS.CUSTOM_REPORTS.COLLEAGUES
      );
    });
  }

  async getCustomReport(id: string): Promise<CustomReportDetail> {
    return withAuthErrorHandling(async () => {
      return this.makeRequest<CustomReportDetail>(
        `${API_CONFIG.ENDPOINTS.CUSTOM_REPORTS.LIST}/${id}`
      );
    });
  }

  async createCustomReport(input: {
    name: string;
    memberIds: string[];
    products: Array<{ productId: number; allocatedNotional: number }>;
  }): Promise<CustomReportDetail> {
    return withAuthErrorHandling(async () => {
      const response = await authService.makeAuthenticatedRequest<{
        success: boolean;
        data: CustomReportDetail;
        error?: string;
      }>(`${this.baseUrl}${API_CONFIG.ENDPOINTS.CUSTOM_REPORTS.LIST}`, {
        method: 'POST',
        body: JSON.stringify(input),
      });
      if (!response.success) {
        throw new Error(response.error || 'Failed to create report');
      }
      return response.data;
    });
  }

  async updateCustomReport(
    id: string,
    input: {
      name?: string;
      memberIds?: string[];
      products?: Array<{ productId: number; allocatedNotional: number }>;
    }
  ): Promise<CustomReportDetail> {
    return withAuthErrorHandling(async () => {
      const response = await authService.makeAuthenticatedRequest<{
        success: boolean;
        data: CustomReportDetail;
        error?: string;
      }>(`${this.baseUrl}${API_CONFIG.ENDPOINTS.CUSTOM_REPORTS.LIST}/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(input),
      });
      if (!response.success) {
        throw new Error(response.error || 'Failed to update report');
      }
      return response.data;
    });
  }

  async deleteCustomReport(id: string): Promise<void> {
    return withAuthErrorHandling(async () => {
      const response = await authService.makeAuthenticatedRequest<{
        success: boolean;
        error?: string;
      }>(`${this.baseUrl}${API_CONFIG.ENDPOINTS.CUSTOM_REPORTS.LIST}/${id}`, {
        method: 'DELETE',
      });
      if (!response.success) {
        throw new Error(response.error || 'Failed to delete report');
      }
    });
  }

  async updateCustomReportProductNotional(
    reportId: string,
    productId: number,
    allocatedNotional: number
  ): Promise<CustomReportDetail> {
    return withAuthErrorHandling(async () => {
      const response = await authService.makeAuthenticatedRequest<{
        success: boolean;
        data: CustomReportDetail;
        error?: string;
      }>(
        `${this.baseUrl}${API_CONFIG.ENDPOINTS.CUSTOM_REPORTS.LIST}/${reportId}/products/${productId}`,
        {
          method: 'PATCH',
          body: JSON.stringify({ allocatedNotional }),
        }
      );
      if (!response.success) {
        throw new Error(response.error || 'Failed to update notional');
      }
      return response.data;
    });
  }

  async removeCustomReportProduct(reportId: string, productId: number): Promise<CustomReportDetail> {
    return withAuthErrorHandling(async () => {
      const response = await authService.makeAuthenticatedRequest<{
        success: boolean;
        data: CustomReportDetail;
        error?: string;
      }>(
        `${this.baseUrl}${API_CONFIG.ENDPOINTS.CUSTOM_REPORTS.LIST}/${reportId}/products/${productId}`,
        {
          method: 'DELETE',
        }
      );
      if (!response.success) {
        throw new Error(response.error || 'Failed to remove product');
      }
      return response.data;
    });
  }

  async getOwnershipScopeAvailability(): Promise<{ canUseOwnershipScope: boolean }> {
    return withAuthErrorHandling(async () => {
      return this.makeRequest<{ canUseOwnershipScope: boolean }>(
        API_CONFIG.ENDPOINTS.PRODUCTS.OWNERSHIP_SCOPE_AVAILABILITY
      );
    });
  }

  async getUserPreferences(): Promise<{ avatarUrl: string | null; language: 'en' | 'es'; theme: 'light' | 'dark' }> {
    return withAuthErrorHandling(async () => {
      return this.makeRequest<{ avatarUrl: string | null; language: 'en' | 'es'; theme: 'light' | 'dark' }>(
        API_CONFIG.ENDPOINTS.AUTH.PREFERENCES
      );
    });
  }

  async getDashboardLayout(): Promise<DashboardLayoutResponse> {
    return withAuthErrorHandling(async () => {
      return this.makeRequest<DashboardLayoutResponse>(
        API_CONFIG.ENDPOINTS.USERS.DASHBOARD_LAYOUT
      );
    });
  }

  async updateDashboardLayout(layout: DashboardLayout): Promise<DashboardLayoutResponse> {
    return withAuthErrorHandling(async () => {
      const response = await authService.makeAuthenticatedRequest<{
        success: boolean;
        data: DashboardLayoutResponse;
        error?: string | { message?: string };
        message?: string;
      }>(`${this.baseUrl}${API_CONFIG.ENDPOINTS.USERS.DASHBOARD_LAYOUT}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          columnCount: layout.columnCount,
          widgets: layout.widgets.map((widget) => ({
            id: widget.id,
            order: widget.order,
            widthSpan: widget.widthSpan,
          })),
        }),
      });

      if (!response.success) {
        const errorMessage =
          typeof response.error === 'string'
            ? response.error
            : response.error?.message || response.message || 'Failed to save dashboard';
        throw new Error(errorMessage);
      }

      return response.data;
    });
  }

  async updateUserPreferences(input: {
    language?: 'en' | 'es';
    theme?: 'light' | 'dark';
  }): Promise<{ avatarUrl: string | null; language: 'en' | 'es'; theme: 'light' | 'dark' }> {
    return withAuthErrorHandling(async () => {
      const response = await authService.makeAuthenticatedRequest<{
        success: boolean;
        data: { avatarUrl: string | null; language: 'en' | 'es'; theme: 'light' | 'dark' };
        error?: string;
      }>(`${this.baseUrl}${API_CONFIG.ENDPOINTS.AUTH.PREFERENCES}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(input),
      });

      if (!response.success) {
        throw new Error(response.error || 'Failed to update user preferences');
      }

      return response.data;
    });
  }

  async uploadUserAvatar(input: {
    uri: string;
    mimeType: string;
    fileName?: string;
  }): Promise<{ avatarUrl: string | null; language: 'en' | 'es'; theme: 'light' | 'dark' }> {
    return withAuthErrorHandling(async () => {
      const token = await authService.getValidAccessToken();
      if (!token) {
        throw new Error('Authentication required');
      }

      const formData = new FormData();
      const name = input.fileName || `avatar-${Date.now()}.jpg`;
      if (Platform.OS === 'web') {
        const fileResponse = await fetch(input.uri);
        const fileBlob = await fileResponse.blob();
        formData.append('avatar', fileBlob, name);
      } else {
        const avatarFile: { uri: string; name: string; type: string } = {
          uri: input.uri,
          name,
          type: input.mimeType || 'image/jpeg',
        };
        formData.append('avatar', avatarFile as unknown as Blob);
      }

      const response = await fetch(`${this.baseUrl}${API_CONFIG.ENDPOINTS.AUTH.AVATAR}`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
        },
        body: formData,
      });

      const payload = await response.json();
      if (!response.ok || !payload?.success) {
        throw new Error(payload?.message || 'Failed to upload avatar');
      }

      return payload.data;
    });
  }

  async deleteUserAvatar(): Promise<{ avatarUrl: string | null; language: 'en' | 'es'; theme: 'light' | 'dark' }> {
    return withAuthErrorHandling(async () => {
      const response = await authService.makeAuthenticatedRequest<{
        success: boolean;
        data: { avatarUrl: string | null; language: 'en' | 'es'; theme: 'light' | 'dark' };
        error?: string;
      }>(`${this.baseUrl}${API_CONFIG.ENDPOINTS.AUTH.AVATAR}`, {
        method: 'DELETE',
      });

      if (!response.success) {
        throw new Error(response.error || 'Failed to delete avatar');
      }

      return response.data;
    });
  }

  async checkServerHealth(): Promise<boolean> {
    try {
      const response = await fetch(`${this.baseUrl}`);
      return response.ok;
    } catch (error) {
      console.error('Server not available:', error);
      return false;
    }
  }

  async getTopUnderlyingExposure(
    filterType: 'worst' | 'equally' | 'notional' = 'worst',
    includeWarrants: boolean = true,
    ownershipScope: 'group' | 'self' = 'group'
  ): Promise<Array<{ name: string; value: number; color: string; filterType?: string }>> {
    return withAuthErrorHandling(async () => {
      console.log('🔍 API SERVICE - Fetching top underlying exposure with filterType:', filterType, 'includeWarrants:', includeWarrants);
      const params = new URLSearchParams();
      params.append('filterType', filterType);
      params.append('includeWarrants', includeWarrants.toString());
      params.append('ownershipScope', ownershipScope);
      return await this.makeRequest<Array<{ name: string; value: number; color: string; filterType?: string }>>(
        `${API_CONFIG.ENDPOINTS.STATISTICS.TOP_UNDERLYING_EXPOSURE}?${params.toString()}`
      );
    });
  }

  async getProductsList(
    page: number = 1,
    pageSize: number = 20,
    filters?: {
      searchQuery?: string;
      filterType?: 'date' | 'product' | 'category';
      productType?: number;
      assetClass?: string;
      // Dashboard filters
      currency?: string;
      issuer?: string;
      maturityQuarter?: string;
      underlying?: string;
      // Events filters
      eventType?: string;
      weekStart?: string;
      eventDate?: string;
      eventBucket?: 'nextWeek' | 'nextMaturity';
      nearBarrier?: boolean;
      riskType?: 'barrier' | 'barrierHit' | 'lowStrike';
      ownershipScope?: 'group' | 'self';
      /** Worst-of distance % must be less than this (1–99). Omit for no cap. */
      maxDistancePct?: number;
      // Multiple selection filters
      productTypes?: string[];
      issuers?: string[];
      includeWarrants?: boolean;
    }
  ): Promise<{
    data: Array<{
      PROId: number;
      name: string;
      isin: string;
      valor?: string;
      bid: number;
      ask: number;
      ccy: string;
      issuer?: string;
      productType?: number;
      productCode?: string;
      productTypeDisplay?: string;
      barrierStyleName?: string | null;
      initialFixing?: string;
      finalFixing?: string;
      redemptionDate?: string;
      tradedNotional?: number | null;
      currentNotional?: number | null;
      livePrice?: boolean;
    }>;
    pagination: {
      page: number;
      pageSize: number;
      totalCount: number;
      totalPages: number;
      hasNextPage: boolean;
      hasPreviousPage: boolean;
    };
  }> {
    return withAuthErrorHandling(async () => {
      const params = new URLSearchParams();
      params.append('page', page.toString());
      params.append('pageSize', pageSize.toString());
      
      if (filters?.searchQuery) {
        params.append('searchQuery', filters.searchQuery);
      }
      if (filters?.filterType) {
        params.append('filterType', filters.filterType);
      }
      if (filters?.productType) {
        params.append('productType', filters.productType.toString());
      }
      if (filters?.assetClass) {
        params.append('assetClass', filters.assetClass);
      }
      
      // Dashboard filters
      if (filters?.currency) {
        params.append('currency', filters.currency);
      }
      if (filters?.issuer) {
        params.append('issuer', filters.issuer);
      }
      
      // Multiple selection filters
      if (filters?.productTypes && filters.productTypes.length > 0) {
        filters.productTypes.forEach((type) => {
          params.append('productTypes', type);
        });
      }
      if (filters?.issuers && filters.issuers.length > 0) {
        filters.issuers.forEach((issuer) => {
          params.append('issuers', issuer);
        });
      }
      if (filters?.maturityQuarter) {
        params.append('maturityQuarter', filters.maturityQuarter);
      }
      if (filters?.underlying) {
        params.append('underlying', filters.underlying);
      }
      
      // Events filters
      if (filters?.eventType) {
        params.append('eventType', filters.eventType);
      }
      if (filters?.weekStart) {
        params.append('weekStart', filters.weekStart);
      }
      if (filters?.eventDate) {
        params.append('eventDate', filters.eventDate);
      }
      if (filters?.eventBucket) {
        params.append('eventBucket', filters.eventBucket);
      }
      if (filters?.nearBarrier) {
        params.append('nearBarrier', 'true');
      }
      if (filters?.riskType) {
        params.append('riskType', filters.riskType);
      }
      if (filters?.ownershipScope) {
        params.append('ownershipScope', filters.ownershipScope);
      }
      if (
        filters?.maxDistancePct != null &&
        Number.isFinite(filters.maxDistancePct) &&
        filters.maxDistancePct >= 1 &&
        filters.maxDistancePct < 100
      ) {
        params.append('maxDistancePct', String(Math.round(filters.maxDistancePct)));
      }
      if (filters?.includeWarrants !== undefined) {
        params.append('includeWarrants', filters.includeWarrants.toString());
      }

      const url = `${API_CONFIG.ENDPOINTS.PRODUCTS.LIST}?${params.toString()}`;
      console.log(`🌐 API URL: ${url}`);
      console.log(`🌐 API Params: page=${page}, pageSize=${pageSize}`);

      const response = await this.makeRequest<{
        data: Array<{
          PROId: number;
          name: string;
          isin: string;
          valor?: string;
          bid: number;
          ask: number;
          ccy: string;
          issuer?: string;
          productType?: number;
          productCode?: string;
          productTypeDisplay?: string;
          barrierStyleName?: string | null;
          initialFixing?: string;
          finalFixing?: string;
          redemptionDate?: string;
          tradedNotional?: number | null;
          currentNotional?: number | null;
        }>;
        pagination: {
          page: number;
          pageSize: number;
          totalCount: number;
          totalPages: number;
          hasNextPage: boolean;
          hasPreviousPage: boolean;
        };
      }>(url);
      
      if (Array.isArray(response)) {
        const fullResponse = await authService.makeAuthenticatedRequest<{ 
          success: boolean; 
          data: any[]; 
          pagination: any;
          error?: string;
        }>(`${this.baseUrl}${API_CONFIG.ENDPOINTS.PRODUCTS.LIST}?${params.toString()}`);
        
        if (!fullResponse.success) {
          throw new Error(fullResponse.error || 'Server response error');
        }
        
        return {
          data: fullResponse.data,
          pagination: fullResponse.pagination
        };
      }
      
      return response;
    });
  }

  async getProductByIsin(
    isin: string,
    ownershipScope: 'group' | 'self' = 'group'
  ): Promise<any> {
    return withAuthErrorHandling(async () => {
      const params = new URLSearchParams();
      params.append('ownershipScope', ownershipScope);
      return this.makeRequest<any>(
        `${API_CONFIG.ENDPOINTS.PRODUCTS.LIST}/${isin}?${params.toString()}`
      );
    });
  }

  async createProductShareLink(
    isin: string,
    ownershipScope: 'group' | 'self' = 'group'
  ): Promise<ProductShareLinkCreated> {
    return withAuthErrorHandling(async () => {
      const params = new URLSearchParams();
      params.append('ownershipScope', ownershipScope);

      const response = await authService.makeAuthenticatedRequest<{
        success: boolean;
        data: ProductShareLinkCreated;
        message?: string;
      }>(`${this.baseUrl}${API_CONFIG.ENDPOINTS.PRODUCTS.LIST}/${isin}/share?${params.toString()}`, {
        method: 'POST',
        body: JSON.stringify({}),
      });

      if (!response.success) {
        throw new Error(response.message || 'Failed to create share link');
      }

      return response.data;
    });
  }

  async getProductPriceHistory(
    isin: string,
    period: 'day' | 'month' | 'year' | 'all' = 'all',
    ownershipScope: 'group' | 'self' = 'group'
  ): Promise<Array<{ date: string; price: number; badPrice: number }>> {
    return withAuthErrorHandling(async () => {
      const params = new URLSearchParams();
      params.append('period', period);
      params.append('ownershipScope', ownershipScope);
      return this.makeRequest<Array<{ date: string; price: number; badPrice: number }>>(
        `${API_CONFIG.ENDPOINTS.PRODUCTS.LIST}/${isin}/history?${params.toString()}`
      );
    });
  }

  async getUnderlyingsPriceHistory(
    isin: string,
    period: 'day' | 'month' | 'year' | 'all' = 'all',
    ownershipScope: 'group' | 'self' = 'group'
  ): Promise<Array<{ name: string; isin: string; initialFixingLevel: number; history: Array<{ date: string; price: number }> }>> {
    return withAuthErrorHandling(async () => {
      const params = new URLSearchParams();
      params.append('period', period);
      params.append('ownershipScope', ownershipScope);
      return this.makeRequest<Array<{ name: string; isin: string; initialFixingLevel: number; history: Array<{ date: string; price: number }> }>>(
        `${API_CONFIG.ENDPOINTS.PRODUCTS.LIST}/${isin}/underlyings-history?${params.toString()}`
      );
    });
  }

  async getEarlyRedemption(
    isin: string,
    ownershipScope: 'group' | 'self' = 'group'
  ): Promise<Array<{ observationDate: string; redemptionAmount?: string; redemptionLevel?: number; wasTriggered: boolean; periodNumber: number }>> {
    return withAuthErrorHandling(async () => {
      const params = new URLSearchParams();
      params.append('ownershipScope', ownershipScope);
      return this.makeRequest<Array<{ observationDate: string; redemptionAmount?: string; redemptionLevel?: number; wasTriggered: boolean; periodNumber: number }>>(
        `${API_CONFIG.ENDPOINTS.PRODUCTS.LIST}/${isin}/early-redemption?${params.toString()}`
      );
    });
  }

  async downloadTermSheet(isin: string, termSheetFileName?: string): Promise<void> {
    return withAuthErrorHandling(async () => {
      const token = await authService.getValidAccessToken();
      
      if (!token) {
        throw new Error('No authentication token available');
      }

      const url = `${this.baseUrl}${API_CONFIG.ENDPOINTS.PRODUCTS.LIST}/${isin}/term-sheet`;
      
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        let errorMessage = `Error ${response.status}: Failed to download term sheet`;
        try {
          const errorData = await response.json();

          if (errorData.message) {
            errorMessage = errorData.message;
          } else if (errorData.error) {
            errorMessage = errorData.error;
          }
        } catch (e) {
          // If response is not JSON, use status text
          if (response.statusText) {
            errorMessage = `${response.status}: ${response.statusText}`;
          }
        }
        throw new Error(errorMessage);
      }

      // Get filename from Content-Disposition header or use provided filename or default
      const contentDisposition = response.headers.get('Content-Disposition');
      
      let filename = termSheetFileName || 'term-sheet.pdf';
      
      if (contentDisposition) {
        // Try to get filename* (RFC 5987 encoded) first
        const filenameStarMatch = contentDisposition.match(/filename\*=UTF-8''([^;]+)/);
        if (filenameStarMatch) {
          try {
            filename = decodeURIComponent(filenameStarMatch[1]);
          } catch (e) {
            console.log('📥 Error decoding filename*:', e);
          }
        } else {
          // Fallback to regular filename (inside quotes)
          const filenameMatch = contentDisposition.match(/filename="([^"]+)"/);
          if (filenameMatch) {
            filename = filenameMatch[1];
          } else {
            // Try without quotes
            const filenameMatchNoQuotes = contentDisposition.match(/filename=([^;]+)/);
            if (filenameMatchNoQuotes) {
              filename = filenameMatchNoQuotes[1].trim();
            }
          }
        }
      }

      // Handle download based on platform
      if (typeof window !== 'undefined' && window.document) {
        // Web platform
        const blob = await response.blob();
        const blobUrl = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = blobUrl;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(blobUrl);
      } else {
        // React Native - would need expo-file-system or similar
        // For now, throw an error indicating it's not implemented
        throw new Error('File download not yet implemented for mobile platforms');
      }
    });
  }

  // User Management
  async getUsers(): Promise<
    Array<{
      id: string;
      email: string;
      role: string;
      isActive: boolean;
      createdAt: string;
      companyName?: string | null;
    }>
  > {
    return withAuthErrorHandling(async () => {
      const response = await authService.makeAuthenticatedRequest<{
        success: boolean;
        data: Array<{
          id: string;
          email: string;
          role: string;
          isActive: boolean;
          createdAt: string;
          companyName?: string | null;
        }>;
        error?: string;
      }>(
        `${this.baseUrl}/api/auth/users`
      );
      
      if (!response.success) {
        throw new Error(response.error || 'Failed to fetch users');
      }
      
      return response.data;
    });
  }

  async createUser(email: string, role: string = 'client'): Promise<{ id: string; email: string; role: string; isActive: boolean; createdAt: string }> {
    return withAuthErrorHandling(async () => {
      console.log('📤 API: Creating user:', { email, role });
      console.log('📤 API: URL:', `${this.baseUrl}/api/auth/users`);
      
      const response = await authService.makeAuthenticatedRequest<{ success: boolean; data: { id: string; email: string; role: string; isActive: boolean; createdAt: string }; error?: string }>(
        `${this.baseUrl}/api/auth/users`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ email, role }),
        }
      );
      
      console.log('📥 API: Response:', response);
      
      if (!response.success) {
        console.error('❌ API: Request failed:', response.error);
        throw new Error(response.error || 'Failed to create user');
      }
      
      console.log('✅ API: User created successfully');
      return response.data;
    });
  }

  async getAnalyticsOverviewMonthly(year: number, month: number): Promise<MonthlyOverviewAnalytics> {
    return withAuthErrorHandling(async () => {
      const params = new URLSearchParams({
        year: String(year),
        month: String(month),
      });
      return this.makeRequest<MonthlyOverviewAnalytics>(
        `${API_CONFIG.ENDPOINTS.ANALYTICS.OVERVIEW_MONTHLY}?${params.toString()}`
      );
    });
  }

  async getAnalyticsUsersMonthly(year: number, month: number): Promise<UserMonthlyAnalyticsRow[]> {
    return withAuthErrorHandling(async () => {
      const params = new URLSearchParams({
        year: String(year),
        month: String(month),
      });
      return this.makeRequest<UserMonthlyAnalyticsRow[]>(
        `${API_CONFIG.ENDPOINTS.ANALYTICS.USERS_MONTHLY}?${params.toString()}`
      );
    });
  }

  async getAnalyticsUserMonthly(
    userId: string,
    year: number,
    month: number
  ): Promise<UserMonthlyAnalyticsDetail> {
    return withAuthErrorHandling(async () => {
      const params = new URLSearchParams({
        year: String(year),
        month: String(month),
      });
      return this.makeRequest<UserMonthlyAnalyticsDetail>(
        `${API_CONFIG.ENDPOINTS.ANALYTICS.USER_MONTHLY(userId)}?${params.toString()}`
      );
    });
  }

  async resendActivationEmail(userId: string): Promise<void> {
    return withAuthErrorHandling(async () => {
      const response = await authService.makeAuthenticatedRequest<{ success: boolean; error?: string }>(
        `${this.baseUrl}/api/auth/resend-activation`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ userId }),
        }
      );
      
      if (!response.success) {
        throw new Error(response.error || 'Failed to resend activation email');
      }
    });
  }

  async getFavourites(): Promise<FavouriteItem[]> {
    return withAuthErrorHandling(async () => {
      return this.makeRequest<FavouriteItem[]>(API_CONFIG.ENDPOINTS.FAVOURITES.LIST);
    });
  }

  async getFavouritesWatchlist(
    ownershipScope: OwnershipScope = 'group'
  ): Promise<WatchlistItem[]> {
    return withAuthErrorHandling(async () => {
      const params = new URLSearchParams();
      params.append('ownershipScope', ownershipScope);
      return this.makeRequest<WatchlistItem[]>(
        `${API_CONFIG.ENDPOINTS.FAVOURITES.WATCHLIST}?${params.toString()}`
      );
    });
  }

  async addFavourite(request: AddFavouriteRequest): Promise<FavouriteCreatedResponse> {
    return withAuthErrorHandling(async () => {
      const ownershipScope = request.ownershipScope ?? 'group';
      const params = new URLSearchParams();
      params.append('ownershipScope', ownershipScope);

      const response = await authService.makeAuthenticatedRequest<{
        success: boolean;
        data: FavouriteCreatedResponse;
        error?: string | { message?: string };
      }>(`${this.baseUrl}${API_CONFIG.ENDPOINTS.FAVOURITES.LIST}?${params.toString()}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ isin: request.isin, ownershipScope }),
      });

      if (!response.success) {
        const errorMessage =
          typeof response.error === 'string'
            ? response.error
            : response.error?.message || 'Failed to add favourite';
        throw new Error(errorMessage);
      }

      return response.data;
    });
  }

  async removeFavourite(isin: string): Promise<void> {
    return withAuthErrorHandling(async () => {
      const response = await authService.makeAuthenticatedRequest<{
        success: boolean;
        message?: string;
        error?: string | { message?: string };
      }>(`${this.baseUrl}${API_CONFIG.ENDPOINTS.FAVOURITES.LIST}/${encodeURIComponent(isin)}`, {
        method: 'DELETE',
      });

      if (!response.success) {
        const errorMessage =
          typeof response.error === 'string'
            ? response.error
            : response.error?.message || 'Failed to remove favourite';
        throw new Error(errorMessage);
      }
    });
  }

  async getNotifications(): Promise<InAppNotification[]> {
    return withAuthErrorHandling(async () => {
      const cacheBuster = `t=${Date.now()}`;
      return this.makeRequest<InAppNotification[]>(
        `${API_CONFIG.ENDPOINTS.NOTIFICATIONS.LIST}?${cacheBuster}`
      );
    });
  }

  async markNotificationAsRead(notificationId: string): Promise<void> {
    return withAuthErrorHandling(async () => {
      const response = await authService.makeAuthenticatedRequest<{ success: boolean; message?: string; error?: string }>(
        `${this.baseUrl}${API_CONFIG.ENDPOINTS.NOTIFICATIONS.MARK_READ.replace(':id', notificationId)}`,
        {
          method: 'PATCH',
        }
      );
      if (!response.success) {
        throw new Error(response.error || 'Failed to mark notification as read');
      }
    });
  }

  async markAllNotificationsAsRead(): Promise<void> {
    return withAuthErrorHandling(async () => {
      const response = await authService.makeAuthenticatedRequest<{ success: boolean; message?: string; error?: string }>(
        `${this.baseUrl}${API_CONFIG.ENDPOINTS.NOTIFICATIONS.MARK_ALL_READ}`,
        {
          method: 'PATCH',
        }
      );
      if (!response.success) {
        throw new Error(response.error || 'Failed to mark all notifications as read');
      }
    });
  }

  async deleteAllNotifications(): Promise<number> {
    return withAuthErrorHandling(async () => {
      const response = await authService.makeAuthenticatedRequest<{
        success: boolean;
        data: { deletedCount: number };
        error?: string;
      }>(`${this.baseUrl}${API_CONFIG.ENDPOINTS.NOTIFICATIONS.DELETE_ALL}`, {
        method: 'DELETE',
      });
      if (!response.success) {
        throw new Error(response.error || 'Failed to delete notifications');
      }
      return Number(response.data?.deletedCount || 0);
    });
  }

  async getNotificationPreferences(): Promise<NotificationPreferences> {
    return withAuthErrorHandling(async () => {
      return this.makeRequest<NotificationPreferences>(API_CONFIG.ENDPOINTS.NOTIFICATIONS.PREFERENCES);
    });
  }

  async getNotificationEmailPreview(): Promise<NotificationEmailPreview> {
    return withAuthErrorHandling(async () => {
      return this.makeRequest<NotificationEmailPreview>(API_CONFIG.ENDPOINTS.NOTIFICATIONS.EMAIL_PREVIEW);
    });
  }

  async updateNotificationPreferences(input: Partial<NotificationPreferences>): Promise<NotificationPreferences> {
    return withAuthErrorHandling(async () => {
      const response = await authService.makeAuthenticatedRequest<{
        success: boolean;
        data: NotificationPreferences;
        error?: string;
      }>(`${this.baseUrl}${API_CONFIG.ENDPOINTS.NOTIFICATIONS.PREFERENCES}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(input),
      });

      if (!response.success) {
        throw new Error(response.error || 'Failed to update notification preferences');
      }

      return response.data;
    });
  }

}

export const apiService = new ApiService();

export const setApiBaseUrl = (newUrl: string) => {
  return new ApiService(newUrl);
}; 
